<script setup>
// One post in the Social posts block, drawn with its platform's own
// embed. YouTube and TikTok are players in a frame of the site's own
// corners; Instagram, Facebook and X are their platforms' cards, which
// only those platforms can draw. Every one starts as a plain link, so a
// blocked script leaves something a visitor can still follow.
import { computed, onMounted, ref } from 'vue';
import { SOCIAL_PLATFORM_LABELS } from './socialPosts.js';
import { processFacebook, processInstagram, processX } from './socialScripts.js';

const props = defineProps({
    post: { type: Object, required: true },
});

const el = ref(null);
const playing = ref(false);
const label = computed(() => SOCIAL_PLATFORM_LABELS[props.post.platform] || 'the post');
const fbWidth = ref(500);

onMounted(() => {
    const p = props.post.platform;
    if (p === 'instagram') processInstagram().catch(() => {});
    else if (p === 'x') processX(el.value).catch(() => {});
    else if (p === 'facebook') {
        // Facebook's post plugin takes a width between 350 and 750, not
        // "fill the column"; measured once at mount.
        const w = Math.round(el.value?.clientWidth || 500);
        fbWidth.value = Math.max(350, Math.min(750, w));
        processFacebook(el.value).catch(() => {});
    }
});
</script>

<template>
    <div ref="el" class="tiq-sp" :class="[`tiq-sp--${post.platform}`, { 'tiq-sp--vertical': post.vertical || post.platform === 'tiktok' }]">
        <!-- YouTube: a picture and a play button until pressed, so a page
             of six videos does not load six players. -->
        <div v-if="post.platform === 'youtube'" class="tiq-sp__frame">
            <iframe v-if="playing" class="tiq-sp__player" :src="`https://www.youtube-nocookie.com/embed/${post.id}?autoplay=1&rel=0`"
                    title="YouTube video" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowfullscreen></iframe>
            <button v-else type="button" class="tiq-sp__poster" :aria-label="`Play the video`" @click="playing = true">
                <img :src="`https://i.ytimg.com/vi/${post.id}/hqdefault.jpg`" alt="" loading="lazy" />
                <span class="tiq-sp__play" aria-hidden="true"><svg viewBox="0 0 24 24" width="28" height="28"><path d="M8 5v14l11-7z" fill="currentColor"/></svg></span>
            </button>
        </div>

        <div v-else-if="post.platform === 'tiktok'" class="tiq-sp__frame">
            <iframe class="tiq-sp__player" :src="`https://www.tiktok.com/player/v1/${post.id}?description=1&rel=0`" loading="lazy"
                    title="TikTok video" allow="encrypted-media; fullscreen; picture-in-picture" allowfullscreen></iframe>
        </div>

        <blockquote v-else-if="post.platform === 'instagram'" class="instagram-media tiq-sp__card" :data-instgrm-permalink="post.url" data-instgrm-version="14">
            <a :href="post.url" target="_blank" rel="noopener">View this post on Instagram</a>
        </blockquote>

        <blockquote v-else-if="post.platform === 'x'" class="twitter-tweet tiq-sp__card" data-dnt="true">
            <a :href="post.url" target="_blank" rel="noopener">View this post on X</a>
        </blockquote>

        <div v-else-if="post.platform === 'facebook'" class="fb-post tiq-sp__card" :data-href="post.url" :data-width="fbWidth" data-show-text="true">
            <blockquote :cite="post.url" class="fb-xfbml-parse-ignore">
                <a :href="post.url" target="_blank" rel="noopener">View this post on Facebook</a>
            </blockquote>
        </div>

        <a v-else :href="post.url" target="_blank" rel="noopener">View on {{ label }}</a>
    </div>
</template>

<style scoped>
.tiq-sp { min-width: 0; max-width: 100%; }
.tiq-sp__frame {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    border-radius: var(--ssf-radius, 8px);
    background: #000;
}
.tiq-sp--vertical .tiq-sp__frame { aspect-ratio: 9 / 16; max-width: 340px; margin: 0 auto; }
.tiq-sp__player, .tiq-sp__poster { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
.tiq-sp__poster { padding: 0; cursor: pointer; background: #000; }
.tiq-sp__poster img { width: 100%; height: 100%; object-fit: cover; display: block; opacity: 0.92; transition: opacity 0.15s; }
.tiq-sp__poster:hover img { opacity: 1; }
.tiq-sp__play {
    position: absolute;
    left: 50%;
    top: 50%;
    width: 64px;
    height: 64px;
    margin: -32px 0 0 -32px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: var(--ssf-accent, #1a1a1a);
    color: #fff;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
}
/* The platforms' own cards set fixed minimum widths that are wider than
   a phone column; inside a grid cell they must give way. */
.tiq-sp :deep(iframe.instagram-media),
.tiq-sp :deep(.instagram-media),
.tiq-sp :deep(.twitter-tweet),
.tiq-sp :deep(.fb-post),
.tiq-sp :deep(.fb-post span),
.tiq-sp :deep(.fb-post iframe) { min-width: 0 !important; max-width: 100% !important; }
.tiq-sp__card { margin: 0 !important; }
.tiq-sp__card a, .tiq-sp > a {
    display: block;
    padding: 16px;
    border: 1px solid var(--ssf-line, rgba(0, 0, 0, 0.12));
    border-radius: var(--ssf-radius, 8px);
    color: inherit;
    font-weight: 600;
}
</style>
